<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useMonitoringStore } from '@/stores/monitoring'
import { useRoute, useRouter } from 'vue-router'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import ProjectTabs from '@/pages/Project/Components/ProjectTabs.vue'
import SelectedAreaChip from '@/pages/Project/Components/SelectedAreaChip.vue'
import { fetchAlerts, markAlertsRead } from '@/services/monitoringApi'
import { useSelectedArea } from '@/composables/useAreaSelection'
import { formatTimeFromNow } from '@/utils/datetime'

const CATEGORIES = [
  { key: 'water', label: 'Water' },
  { key: 'heat', label: 'Heat' },
  { key: 'vegetation', label: 'Vegetation' },
  { key: 'carbon', label: 'Carbon' },
  { key: 'imagery', label: 'Imagery' }
]

const SEVERITY_DOT = {
  good: 'bg-green-600',
  watch: 'bg-amber-500',
  alert: 'bg-red-500'
}

const MONITORING_METRICS = ['swc', 'lst', 'ndvi', 'biomass', 'canopyCover', 'carbonDensity']

const route = useRoute()
const router = useRouter()
const monitoringStore = useMonitoringStore()
const orgId = computed(() => route.params.org_id)
const projectId = computed(() => route.params.project_id)
const selectedArea = useSelectedArea(projectId)

const alerts = ref([])
const isLoading = ref(false)
const loadFailed = ref(false)
const category = ref('all')

const selectedCategory = computed(() => (category.value === 'all' ? undefined : category.value))

async function load() {
  isLoading.value = true
  try {
    const data = await fetchAlerts(orgId.value, projectId.value, selectedCategory.value, selectedArea.value?.code)
    alerts.value = data.alerts || []
    loadFailed.value = false
    monitoringStore.setUnreadAlerts(data.unread || 0)
  } catch (e) {
    loadFailed.value = true
    console.error('Failed to load alerts', e)
  } finally {
    isLoading.value = false
  }
}

async function markRead() {
  try {
    await markAlertsRead(orgId.value, projectId.value)
    monitoringStore.setUnreadAlerts(0)
  } catch (e) {
    console.error('Failed to mark alerts read', e)
  }
}

watch(category, load)
watch(() => selectedArea.value?.code, load)

function categoryLabel(key) {
  return CATEGORIES.find((c) => c.key === key)?.label ?? key
}

function isClickable(alert) {
  if (alert.category === 'imagery') return Boolean(alert.feature_code)
  return Boolean(alert.metric && MONITORING_METRICS.includes(alert.metric))
}

function openAlert(alert) {
  const params = { org_id: orgId.value, project_id: projectId.value }
  if (alert.category === 'imagery' && alert.feature_code) {
    router.push({ name: 'ProjectImagery', params, query: { capture: alert.feature_code } })
    return
  }
  if (alert.metric && MONITORING_METRICS.includes(alert.metric)) {
    router.push({ name: 'ProjectMonitoring', params, query: { metric: alert.metric } })
  }
}

// Incoming WS alerts bump monitoring/alertTick (committed by wsHandler).
watch(() => monitoringStore.alertTick, async () => {
  await load()
  await markRead()
})

onMounted(async () => {
  await load()
  await markRead()
})
</script>

<template>
  <ProjectTabs />
  <SelectedAreaChip />

  <div class="px-3 py-2 space-y-3">
    <div class="flex items-center justify-between gap-2">
      <div class="text-sm font-medium">Recent alerts</div>
      <Select v-model="category">
        <SelectTrigger class="h-8 w-[130px] text-xs">
          <SelectValue placeholder="Category" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="all">All</SelectItem>
            <SelectItem v-for="c in CATEGORIES" :key="c.key" :value="c.key">
              {{ c.label }}
            </SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>

    <div
      v-if="loadFailed && !isLoading"
      class="rounded-lg border bg-card px-3 py-6 text-center text-sm text-muted-foreground"
    >
      Couldn't load alerts — check your connection and try again
    </div>

    <div
      v-else-if="!alerts.length && !isLoading"
      class="rounded-lg border bg-card px-3 py-6 text-center text-sm text-muted-foreground"
    >
      No alerts yet
    </div>

    <div
      v-for="alert in alerts"
      :key="alert.code"
      class="rounded-lg border bg-card p-3"
      :class="isClickable(alert) ? 'cursor-pointer transition-colors hover:border-foreground/30' : ''"
      @click="openAlert(alert)"
    >
      <div class="flex items-center gap-2">
        <span
          class="h-2 w-2 shrink-0 rounded-full"
          :class="SEVERITY_DOT[alert.severity] || SEVERITY_DOT.watch"
        />
        <span class="truncate text-sm font-semibold">{{ alert.title }}</span>
        <span class="ml-auto shrink-0 text-[11px] text-muted-foreground">
          {{ formatTimeFromNow(alert.created_at) }}
        </span>
      </div>
      <div class="mt-1 text-xs text-muted-foreground">{{ alert.message }}</div>
      <div class="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span>{{ categoryLabel(alert.category) }}</span>
        <span
          v-if="alert.feature_name"
          class="rounded-full bg-muted px-1.5 py-0.5 font-medium"
        >
          {{ alert.feature_name }}
        </span>
      </div>
    </div>
  </div>
</template>
